/**
 * The ONE parsed-address shape, and the ONE projection onto CRM columns.
 *
 * This module is the reason the address lane exists. A host app gains
 * independent ways to turn "user said an address" into five database columns —
 * a person typing into a form, and an AI assistant resolving an address from
 * chat — and if each parsed the provider payload its own way, a record created
 * one way would be shaped differently from one created the other. Same table,
 * same columns, two spellings of California. `toCrmAddressFields` is the
 * single funnel every caller goes through.
 *
 * PURITY: no I/O, no database client, no React, no host-app imports. That is
 * deliberate — this file is written to be lifted into a shared package
 * unchanged (and now is one).
 *
 * NORMALIZATION CHOICES, stated once so every caller inherits them:
 *  - `state` and `country` are SHORT codes ("CA", "US"). A canonical form is
 *    only canonical if it picks one; "CA" matches US mailing convention.
 *  - `zipCode` carries the +4 when Google supplies a `postal_code_suffix`.
 *  - Everything is a plain `string`, empty when absent — never `null`. The CRM
 *    columns a host app writes this into are typically nullable, but a parsed
 *    address models "the provider had no value" as "", which keeps the type
 *    free of a second absent-ness case.
 */

import { z } from "zod";

/**
 * A fully parsed address plus the provenance needed to talk about it.
 *
 * The first five fields map 1:1 onto a CRM's `contacts` / `companies`-style
 * address columns. The rest are provenance: `formattedAddress` is what Google
 * would print, `placeId` identifies the place for a later lookup, and the
 * coordinates are what an admin address map would geocode. Provenance is
 * carried so a caller can show its work — it is NOT written to the CRM by
 * `toCrmAddressFields` (see the note on hidden fields below).
 */
export const canonicalAddressSchema = z.object({
  /** Street line, including any unit: "1600 Amphitheatre Pkwy #1012". */
  address: z.string(),
  city: z.string(),
  /** Two-letter state/province code where one exists: "CA", "TN". */
  state: z.string(),
  /** "94043", or "94043-1351" when Google supplies the +4 suffix. */
  zipCode: z.string(),
  /** ISO-3166 alpha-2: "US". */
  country: z.string(),
  formattedAddress: z.string(),
  placeId: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});

export type CanonicalAddress = z.infer<typeof canonicalAddressSchema>;

/**
 * The CRM projection: exactly the five nullable columns most CRM tables carry
 * for a postal address, spelled the way a database typically spells them
 * (`zip_code`, not `zipCode`).
 */
export interface CrmAddressFields {
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

/** The CRM column names, for callers that need to iterate or reset them. */
export const CRM_ADDRESS_FIELD_NAMES = [
  "address",
  "city",
  "state",
  "zip_code",
  "country",
] as const satisfies readonly (keyof CrmAddressFields)[];

/**
 * THE consolidation point. Every surface that stores an address from a place
 * lookup — a contact form, a company form, an AI tool — calls this and writes
 * what it returns.
 *
 * Deliberately total and lossy: it drops `placeId`, the coordinates and the
 * formatted string. That is not an oversight — a form that quietly carried
 * `place_id` / `lat` / `lng` in hidden inputs would be seeding required-ish
 * state the user cannot see or correct. The five columns the user can see and
 * edit are the five columns a host app should write.
 */
export function toCrmAddressFields(address: CanonicalAddress): CrmAddressFields {
  return {
    address: address.address,
    city: address.city,
    state: address.state,
    zip_code: address.zipCode,
    country: address.country,
  };
}

/**
 * True when the parse produced something worth writing.
 *
 * A place can resolve with a name and coordinates but no street components at
 * all (a park, a mountain, a whole city). Writing that over a person's typed
 * address would be a silent downgrade, so callers check this first.
 */
export function hasUsableAddress(address: CanonicalAddress): boolean {
  return address.address.length > 0 || address.city.length > 0;
}
